<template>
  <div class="organizeAssetGrid"
    ref="contentList"
    v-loading="assetsLoading && !loadingNewPage"
  >
    <div class="organizeAssetGrid-item"
      v-for="(asset, idx) in assets"
      :key="`${asset.pageUrlId}-${idx}`"
    >
      <asset-card :asset="asset"
        selectable
        :index="idx"
        :selected="assetIsSelected(idx)"
        @select="onAssetSelect"
        @shiftSelect="onAssetShiftSelect"
      ></asset-card>
    </div>

    <div class="organizeAssetGrid-item"
      v-for="(i, idx) in Array.from({length: (assets.length % 3) + 3}, (x, i) => i)"
      :key="`placeholder-${idx}`"
      v-show="assetsLoading || loadingNewPage"
    >
      <placeholder-card></placeholder-card>
    </div>

    <div class="organizeAssetGrid-noAssets"
      v-show="assets.length < 1 && !assetsLoading"
    >
      <div class="organizeAssetGrid-noAssets-logo">
        <p>Sorry! No results were found.</p>
      </div>
    </div>
  </div>
</template>

<script>
import AssetCard from './AssetCard'
import PlaceholderCard from '@/components/Card/PlaceholderCard'
import { includes, without, range, uniq } from 'lodash'

export default {
  name: 'OrganizeAssetGrid',

  components: {
    AssetCard,
    PlaceholderCard
  },

  props: {
    assets: {
      type: Array,
      default: () => []
    },
    selectedAssetIndexes: {
      type: Array,
      default: () => []
    },
    hasMoreAssets: {
      type: Boolean,
      default: false
    },
    assetsLoading: {
      type: Boolean,
      default: false
    },
    loadingNewPage: {
      type: Boolean,
      default: false
    }
  },

  computed: {
    computedSelectedAssetIndexes: {
      get () {
        return this.selectedAssetIndexes
      },
      set (value) {
        this.$emit('selectedAssetIndexesChanged', value)
      }
    }
  },

  methods: {
    assetIsSelected (idx) {
      return includes(this.computedSelectedAssetIndexes, idx)
    },
    onAssetSelect (idx) {
      if (this.assetIsSelected(idx)) {
        this.computedSelectedAssetIndexes = without(this.computedSelectedAssetIndexes, idx)
      } else {
        const newIndexes = this.computedSelectedAssetIndexes.slice()

        newIndexes.push(idx)
        newIndexes.sort((a, b) => a - b)
        this.computedSelectedAssetIndexes = newIndexes
      }
    },
    onAssetShiftSelect (idx) {
      const lastSelectedIndex = this.computedSelectedAssetIndexes
        .reduce((acc, val) => {
          if (val > acc && val < idx) {
            return val
          } else {
            return acc
          }
        }, 0)

      const newSelectedIndexes = range(lastSelectedIndex, idx + 1)

      const selectedIndexesCopy = this.computedSelectedAssetIndexes.slice()
      const mergedIndexes = selectedIndexesCopy.concat(newSelectedIndexes)
        .filter(idx_ => idx_ <= idx)

      this.computedSelectedAssetIndexes = uniq(mergedIndexes)
    },
    onInfiniteScroll () {
      if (!this.hasMoreAssets) { return }
      const bottomOfWindow = document.documentElement.scrollTop + window.innerHeight >= this.$refs.contentList.offsetHeight - 20
      if (bottomOfWindow && !this.assetsLoading) {
        this.$emit('next-page')
      }
    },
    onShiftClick (e) {
      document.onselectstart = function () {
        return !(e.key === 'Shift' && e.shiftKey)
      }
    }
  },

  mounted () {
    // Prevents text selection on shift click. Found @ https://stackoverflow.com/questions/1527751/disable-text-selection-while-pressing-shift
    ['keyup', 'keydown'].forEach(event => {
      window.addEventListener(event, this.onShiftClick)
    })

    document.addEventListener('scroll', this.onInfiniteScroll)
  },

  beforeDestroy () {
    ['keyup', 'keydown'].forEach(event => {
      window.removeEventListener(event, this.onShiftClick)
    })

    document.removeEventListener('scroll', this.onInfiniteScroll)
  }
}
</script>

<style lang="scss" scoped>
.organizeAssetGrid {
  box-sizing: border-box;
  overflow: hidden;
  display: flex;
  flex-wrap: wrap;

  &-item {
    padding-left: 20px;
    padding-bottom: 19px;
  }

  &-noAssets {
    width: 100%;
    height: 280px;
    display: flex;
    justify-content: center;
    align-items: center;
    font-size: 22px;
    font-weight: 500;

    &-logo {
      height: 200px;
      background: no-repeat center url('../../assets/images/no-results.svg');
      p {
        position: relative;
        top: 52px;
      }
    }
  }
}
</style>
